import { useRef, useState, useEffect } from 'react';
import { useLocale, LOCALE_NAMES } from '../../context/LocaleContext';
import { Icon } from './Icon';

interface LocaleSwitcherProps {
  /** Only renders when there are ≥ 2 available locales */
  availableLocales: string[];
  size?: 'sm' | 'md';
}

export function LocaleSwitcher({ availableLocales, size = 'md' }: LocaleSwitcherProps) {
  const { locale, setLocale, getLocaleName } = useLocale();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Don't render if only one (or zero) locales available
  if (availableLocales.length <= 1) return null;

  const iconSize = size === 'sm' ? 14 : 16;
  const btnClass = size === 'sm'
    ? 'inline-flex items-center gap-1 h-8 px-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
    : 'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';

  return (
    <div className="relative" role="navigation" aria-label="Language switcher">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${getLocaleName(locale)}`}
        className={btnClass}
      >
        <Icon name="globe" size={iconSize} aria-hidden="true" />
        <span>{locale.toUpperCase()}</span>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Select language"
          className="absolute right-0 top-full mt-1 min-w-[8rem] rounded-lg border border-border bg-card shadow-lg py-1 z-50"
        >
          {availableLocales.map((code) => {
            const isActive = code === locale;
            const name = LOCALE_NAMES[code] || code.toUpperCase();
            return (
              <button
                key={code}
                role="option"
                aria-selected={isActive}
                type="button"
                onClick={() => { setLocale(code); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors
                  ${isActive
                    ? 'text-primary font-medium bg-primary/5'
                    : 'text-foreground hover:bg-muted'
                  }`}
              >
                {/* Checkmark for active */}
                <span className={`w-4 ${isActive ? 'text-primary' : 'text-transparent'}`}
                  aria-hidden="true">✓</span>
                <span className="font-mono text-xs text-muted-foreground w-6">{code.toUpperCase()}</span>
                <span>{name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
